import React from 'react';
import { connect } from 'react-redux';
import { mapToNewAccessService } from '../../domain/AccessServiceTypes';
import env from '../../env';
import { accessVersionChoiceSelector } from '../../reducers/videoPage';
import { CARD_TYPE } from '../../ui-components/ContentItemCarousel/ContentItemCarousel';
import ContentItemGrid from '../../ui-components/ContentItemGrid/ContentItemGrid';
import { Heading } from '../../ui-components/ContentList/components/Header/Header.styles';
import ContentList from '../../ui-components/ContentList/ContentList';
import { GridWrapper } from '../../ui-components/PromotionGrid/PromotionGrid.styles';
import SlashHeader from '../../ui-components/SlashHeader/SlashHeader';
import { browserMeasurements } from '../../utils/browserMeasurements';
import { generateListTracking, transformToContentList } from '../../utils/contentoUtils';
import { createGenreStatistics } from '../../utils/contentStatistics';
import { createListTrackingOnClick } from '../../utils/statistics/clientStatistics';
import { errorStatistics } from '../../utils/statistics/universalStatistics';
import { createImageProxyTemplate, formatImageProxyUrl } from '../../utils/svtimage';
import { ReactHelmetPlay } from '../ApplicationHandler/ReactHelmet';
import { PlaceHolder, PLACEHOLDER_LAYOUT } from '../../ui-components/PlaceHolder/PlaceHolder';
import SendStatistics from '../CommonElements/SendStatistics';
import ContentoQuery, { ContentoData, ContentoError, ContentoLoading } from '../Contento/ContentoQuery';
import ErrorContainer from '../ErrorHandler/ErrorContainer';
import { NotFoundContainer } from '../ErrorHandler/index';
import { trackGenrePageListClick } from './CategoryPage.helpers';
import CATEGORY_PAGE_QUERY from './CategoryPageQuery.graphql';
import GenrePageHeader from './GenrePageHeader';
import { RecommendedCarousel, RecommendedShowcase } from './Recommended.styles';
import RelatedCategories from './RelatedCategories';
import { withRouter } from 'next/router';
import { Experiment, Variant } from '@svt/abtest/lib/redux';
import { getContentoAbTestVariants } from '../../utils/abiskoUtils';
import KidsContinueWatchingList from './KidsContinueWatchingList';
import {
    continueWatchingItemsSelector,
    continueWatchingActiveSelector,
} from '../../reducers/continueWatching';
import { deleteContinueWatchingListContents } from '../../actions/continueWatching';
import { createContentoHistory } from '../../utils/continueWatchingUtils';
import { CategoryContinueWatchingList } from './CategoryContinueWatchingList.styles';
import { StyledDescription } from './CategoryPage.styles';
import { TYPES } from '../../ui-components/typography/PlayText/PlayText';
import { regularWhite } from '../../ui-components/design-tokens';

const Description = ({ description }) => {
    if (!description) {
        return null;
    }

    return (
        <StyledDescription type={TYPES.LARGE} color={regularWhite}>
            {description}
        </StyledDescription>
    );
};

const shouldShowContinueWatching = (contentoAbTestVariants, continueWatchingIsActive) => {
    return (
        continueWatchingIsActive &&
        (contentoAbTestVariants || []).some(
            (variant) =>
                variant.project === 'svtplay' &&
                variant.experiment === 'contento-keepwatching-categories-2' &&
                variant.variant === 'with-continue-watching'
        )
    );
};

const CategoryPage = ({
    currentAccessibilityChoice,
    router,
    contentoAbTestVariants,
    continueWatchingItems,
    continueWatchingIsActive,
    deleteContinueWatchingListItems,
}) => {
    const showContinueWatching = shouldShowContinueWatching(contentoAbTestVariants, continueWatchingIsActive);

    return (
        <ContentoQuery
            query={CATEGORY_PAGE_QUERY}
            variables={{
                id: router.query.id,
                includeFullOppetArkiv: env.FLAG_ENABLE_ALL_OA,
                tab: router.query.tab ? router.query.tab : '',
                history: showContinueWatching ? createContentoHistory(continueWatchingItems) : null,
                abTestVariants: contentoAbTestVariants,
            }}
            fetchPolicy="cache-and-network"
            preferGetMethod={!showContinueWatching}
        >
            <ContentoData>
                {(contentoData) => {
                    const category = contentoData?.categoryPage;

                    //if no such category exists, send 404
                    if (!category) {
                        return (
                            <>
                                <NotFoundContainer shouldDispatchHttpStatus={true} baseContainer={false} />
                                <SendStatistics statisticsObject={errorStatistics(404, router.asPath)} />
                            </>
                        );
                    }

                    const metaImage = category.image
                        ? formatImageProxyUrl(
                              createImageProxyTemplate(category.image.id, category.image.changed),
                              'large',
                              650
                          )
                        : null;

                    const selectedTab = router.query.tab
                        ? category.lazyLoadedTabs.find((tab) => tab.slug === router.query.tab)
                        : undefined;
                    const activeTab = selectedTab || category.lazyLoadedTabs[0];
                    const selections = activeTab.selections;

                    const currentAccessibilityChoiceContento = mapToNewAccessService(
                        currentAccessibilityChoice
                    );

                    let statisticsObject;
                    if (typeof window !== 'undefined') {
                        const browserSize = browserMeasurements(window);

                        statisticsObject = createGenreStatistics(
                            router.query.id,
                            category.analyticsIdentifiers.viewId,
                            category.analyticsIdentifiers.viewType,
                            browserSize
                        );
                    }

                    return (
                        <>
                            <ReactHelmetPlay
                                pathForFeeds={`/genre/${router.query.id}`}
                                title={category.heading}
                                description={category.description}
                                keywords={`svt play television ${category.heading}`}
                                image={metaImage}
                            >
                                {metaImage && [
                                    <meta key={'og:image'} property="og:image" content={metaImage} />,
                                    <meta key={'og:image:width'} property="og:image:width" content={650} />,
                                    <meta key={'og:image:height'} property="og:image:height" content={366} />,
                                ]}
                                {metaImage && (
                                    <meta
                                        key="twitter:image:src"
                                        name="twitter:image:src"
                                        content={metaImage}
                                    />
                                )}
                            </ReactHelmetPlay>
                            <SlashHeader text={category.heading} type="h1" dataRt="genre-title" />
                            <Description description={category.description} />
                            {router.query.id === 'barn' && (
                                <Experiment
                                    projectId="svtplay"
                                    slug="contento-keepwatching-categories-2"
                                    defaultVariant="original"
                                    timeout={2000}
                                >
                                    <Variant slug="original">
                                        <KidsContinueWatchingList
                                            useTitle
                                            key="startpage-continue-watching"
                                        />
                                    </Variant>
                                    <Variant slug={'with-continue-watching'}>
                                        <></>
                                    </Variant>
                                </Experiment>
                            )}

                            <GenrePageHeader
                                tabs={category.lazyLoadedTabs}
                                selectedTab={activeTab.slug}
                                location={`/genre/${router.query.id}`}
                            />

                            {selections.map((selection, index) => {
                                const listTracking = generateListTracking(selection);

                                const onItemClick = createListTrackingOnClick(listTracking);

                                const contentListItems = transformToContentList(
                                    selection.items,
                                    currentAccessibilityChoiceContento,
                                    selection.presentation
                                );

                                const components = [];

                                const relatedCategories = selection.categories;

                                if (selection.presentation === 'Grid') {
                                    components.push(
                                        <GridWrapper
                                            className="lp_rec"
                                            data-helix-type="list"
                                            data-rt="grid"
                                            key={selection.id}
                                        >
                                            {selection.selectionType !== 'all' && (
                                                <Heading as="h2">{selection.name}</Heading>
                                            )}

                                            <ContentItemGrid
                                                items={transformToContentList(selection.items)}
                                                location={router.asPath}
                                                onItemClick={(e, itemPosition, svtId) => {
                                                    trackGenrePageListClick(
                                                        e,
                                                        itemPosition,
                                                        svtId,
                                                        selection
                                                    );
                                                }}
                                                shouldUseIntersectionObserver
                                            />
                                        </GridWrapper>
                                    );
                                } else {
                                    switch (selection.selectionType) {
                                        case 'recommended':
                                            components.push(
                                                <section key="genre-startblock" data-helix-type="list">
                                                    <RecommendedCarousel
                                                        key={`recommended-carousel-${selection.id}`}
                                                        items={selection.items.slice(0, 6)}
                                                        editModeActivated={false}
                                                        isInSweden={true}
                                                        cardType={CARD_TYPE.SHOWCASE}
                                                        currentAccessibilityChoiceContento={
                                                            currentAccessibilityChoiceContento
                                                        }
                                                        onItemClick={onItemClick}
                                                    />
                                                    <RecommendedShowcase
                                                        key={`recommended-showcase-${selection.id}`}
                                                        items={selection.items.slice(0, 6)}
                                                        onItemClick={onItemClick}
                                                        currentAccessibilityChoiceContento={
                                                            currentAccessibilityChoiceContento
                                                        }
                                                    />
                                                </section>
                                            );
                                            break;
                                        case 'keepWatching':
                                            components.push(
                                                <CategoryContinueWatchingList
                                                    id={selection.id}
                                                    key={selection.id}
                                                    items={contentListItems}
                                                    presentation={selection.presentation}
                                                    headerText={selection.name}
                                                    listTracking={listTracking}
                                                    expandable
                                                    location={router.asPath}
                                                    onItemClick={onItemClick}
                                                    onDeleteItems={deleteContinueWatchingListItems}
                                                    continueWatchingIsActive={continueWatchingIsActive}
                                                    continueWatchingItems={continueWatchingItems}
                                                    deletableItems
                                                />
                                            );
                                            break;
                                        default:
                                            components.push(
                                                <ContentList
                                                    id={selection.id}
                                                    key={selection.id}
                                                    items={contentListItems}
                                                    presentation={selection.presentation}
                                                    headerText={selection.name}
                                                    listTracking={listTracking}
                                                    expandable
                                                    location={router.asPath}
                                                    onItemClick={onItemClick}
                                                />
                                            );
                                    }

                                    if (relatedCategories.length) {
                                        components.push(
                                            <RelatedCategories
                                                category={router.query.id}
                                                headerText={selection.name}
                                                relatedCategories={relatedCategories}
                                                key={`relatedCategories-${selection.id}`}
                                            />
                                        );
                                    }
                                }

                                return components;
                            })}

                            <SendStatistics statisticsObject={statisticsObject} />
                        </>
                    );
                }}
            </ContentoData>
            <ContentoLoading>
                <PlaceHolder nrOfItems={1} layout={PLACEHOLDER_LAYOUT.HEADER_TEXT} />
                <PlaceHolder nrOfItems={6} />
            </ContentoLoading>

            <ContentoError>
                <>
                    <ErrorContainer pathname={router.asPath} shouldDispatchHttpStatus={true} />
                    <SendStatistics statisticsObject={errorStatistics(500, router.asPath)} />
                </>
            </ContentoError>
        </ContentoQuery>
    );
};

const mapStateToProps = (state, props) => ({
    currentAccessibilityChoice: accessVersionChoiceSelector(state),
    contentoAbTestVariants: getContentoAbTestVariants(state),
    continueWatchingItems: continueWatchingItemsSelector(state),
    continueWatchingIsActive: continueWatchingActiveSelector(state),
});

const mapDispatchToProps = (dispatch) => {
    return {
        deleteContinueWatchingListItems: (deletedItems) =>
            dispatch(deleteContinueWatchingListContents(deletedItems)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(CategoryPage));
